import {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useId,
  useRef,
  useState
} from 'react';
import { FLOATING_LABEL_INPUT_VARIANT } from 'enums';

type FloatingLabelInputSize = 'small' | 'medium' | 'large';
type FloatingLabelInputVariant = `${FLOATING_LABEL_INPUT_VARIANT}`;

interface FloatingLabelInputProps extends Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'placeholder' | 'size'
> {
  placeholder: string;
  label?: string;
  size?: FloatingLabelInputSize;
  variant?: FloatingLabelInputVariant;
  containerClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  error?: string;
  hasError?: boolean;
  showLabel?: boolean;
  dataTour?: string;
}

const sizeClasses = {
  small: {
    padding: '~px-2/3 ~py-1.5/2',
    text: '~text-[0.65rem]/xs',
    label: '~text-[0.4rem]/[0.6rem]'
  },
  medium: {
    padding: '~px-3/4 ~py-2/3',
    text: '~text-xs/base',
    label: '~text-[0.6rem]/xs'
  },
  large: {
    padding: '~px-4/5 ~py-3/4',
    text: '~text-base/lg',
    label: '~text-xs/sm'
  }
};

const variantClasses = {
  default: {
    border: 'border-secondary-400/60',
    bg: 'bg-white dark:bg-secondary-dark-200',
    focusBorder:
      'focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:focus:ring-primary-900',
    hoverBorder: 'hover:border-primary-400',
    labelBg: 'bg-white dark:bg-secondary-dark-400',
    shadow: 'shadow-sm'
  },
  outlined: {
    border: 'border-2 border-primary-300/40 dark:border-primary-400/30',
    bg: 'bg-transparent',
    focusBorder:
      'focus:border-primary-500 focus:border-2 focus:ring-2 focus:ring-primary-500/30',
    hoverBorder: 'hover:border-primary-400',
    labelBg: 'bg-white dark:bg-secondary-dark-200',
    shadow: 'shadow-sm'
  },
  filled: {
    border: 'border-0',
    bg: 'bg-primary-50 dark:bg-secondary-dark-300',
    focusBorder: 'focus:ring-2 focus:ring-primary-500/50',
    hoverBorder: 'hover:bg-primary-100',
    labelBg: 'bg-primary-50 dark:bg-secondary-dark-200',
    shadow: 'shadow-sm'
  },
  minimal: {
    border:
      'border-b border-primary-300/40 dark:border-primary-400/30 rounded-none',
    bg: 'bg-transparent',
    focusBorder:
      'focus:border-b-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
    hoverBorder: 'hover:border-primary-400',
    labelBg: 'bg-white dark:bg-secondary-dark-200',
    shadow: ''
  }
};

const FloatingLabelInput = ({
  placeholder,
  label,
  size = 'medium',
  variant = 'default',
  containerClassName = '',
  inputClassName = '',
  labelClassName = '',
  error,
  hasError = false,
  value,
  defaultValue,
  showLabel = true,
  id,
  onBlur,
  onFocus,
  dataTour,
  ...rest
}: FloatingLabelInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const inputId = id || generatedId;
  const [isFocused, setIsFocused] = useState(false);

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  const shouldShowLabel = showLabel && (isFocused || value !== '');
  const labelText = label || placeholder;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const showErrorBorder = Boolean(error) || hasError;

  return (
    <div
      className={`relative w-full ${containerClassName}`}
      data-tour={dataTour}>
      <label
        htmlFor={inputId}
        className={`absolute left-0 ${value ? 'transition-none animate-fadeIn' : 'transition-all duration-200 ease-in-out'} pointer-events-none z-10 rounded-t ${
          shouldShowLabel
            ? `top-0 sm:-top-0.5 ~left-2/3 ~px-1/1.5 ~leading-3/4 ${currentSize.label} -translate-y-1/2 ${currentVariant.labelBg} opacity-100 text-gray-500 dark:text-secondary-dark-900`
            : `top-1/2 ~left-3/4 ${currentSize.text} -translate-y-1/2 opacity-70`
        } ${labelClassName}`}>
        {labelText}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        value={value}
        defaultValue={defaultValue}
        placeholder={shouldShowLabel ? '' : placeholder}
        className={`w-full ${currentSize.padding} ${variant === FLOATING_LABEL_INPUT_VARIANT.MINIMAL ? '' : 'rounded-md'} border ${
          showErrorBorder
            ? 'border-red-400 focus:border-red-500 focus:ring-red-400/30'
            : `${currentVariant.border} ${currentVariant.focusBorder} ${currentVariant.hoverBorder || ''}`
        } ${currentVariant.bg} ${currentVariant.shadow} ${currentSize.text} text-black dark:text-white placeholder-transparent focus:outline-none transition-all duration-200 cursor-text ${inputClassName}`}
        {...rest}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />

      {error && (
        <small className='absolute -bottom-5 left-0 ~text-[0.625rem]/xs text-red-600 animate-fadeIn line-clamp-1'>
          {error}
        </small>
      )}
    </div>
  );
};

export default FloatingLabelInput;
